<script lang="ts" setup>
import { computed } from 'vue';
import { ProjectType } from '@/gql/graphql';
import { BBadge, BProgress, BProgressBar, BTooltip } from 'bootstrap-vue-next';
import { ComputeTaskUtils } from '@/utils/compute-utils';
import { COMPUTE_TASK_STATUS, COMPUTE_TASK_TYPES } from '@/constants';

// --- Component Properties ---
const props = defineProps<{
  project: ProjectType;
}>();

interface IProjectProgressItem {
  status: string;
  taskNames: string[];
}

const projectProgressItems = computed<IProjectProgressItem[]>(() => {
  let items: IProjectProgressItem[] = [];
  if (!props.project || !props.project.computeTasks) return items;
  props.project.computeTasks?.forEach((task) => {
    // Check if the task has a status and name
    if (!task || !task.status || !task.name || !task.taskType) return;
    // Skip all tasks not of DATA_PIPELINE type (do not list FTP transfers, BCL-to-FASTQ, etc.)
    if (task.taskType !== COMPUTE_TASK_TYPES.DATA_PIPELINE) return;

    // Check if the status already exists in the items array
    const existingItem = items.find((item) => item.status === task.status);
    if (existingItem) {
      existingItem.taskNames.push(task.name);
    } else {
      items.push({ status: task.status, taskNames: [task.name] });
    }
  });

  /* Turn it into an array…  */
  const statusOrder = Object.values(COMPUTE_TASK_STATUS); // ["CREATING", "PENDING", …]

  /* …and then into a { status → index } map for O(1) look-ups */
  const orderMap = Object.fromEntries(
    statusOrder.map((s, idx) => [s, idx])
  ) as Record<string, number>;

  return items.sort(
    (a, b) =>
      (orderMap[a.status] ?? Infinity) - (orderMap[b.status] ?? Infinity)
  );
});
</script>
<template>
  <div>
    <template v-if="projectProgressItems.length === 0">
      <BBadge variant="secondary">No tasks</BBadge>
    </template>
    <template v-else>
      <template v-for="(item, idx) in projectProgressItems" :key="idx">
        <b-progress
          class="my-1"
          max="100"
          :id="`project-${props.project.id}-status-${idx}`"
          :animated="ComputeTaskUtils.progressActive(item.status)"
        >
          <b-progress-bar
            value="100"
            :variant="ComputeTaskUtils.progressVariant(item.status)"
          >
            <small class="px-2">
              {{ item.status }}
            </small>
          </b-progress-bar>
        </b-progress>
        <BTooltip :target="`project-${props.project.id}-status-${idx}`">
          <strong>
            <font-awesome-icon
              :icon="ComputeTaskUtils.statusIcon(item.status)"
              :class="ComputeTaskUtils.statusIconClass(item.status)"
            />
            {{ item.status }}
          </strong>
          <hr />
          <small>{{ item.taskNames.join(', ') }}</small>
        </BTooltip>
      </template>
    </template>
  </div>
</template>
